import { mdiCalendarClockOutline, mdiMinusCircleOutline } from "@mdi/js"
import type {
  AppointmentPlanningCustomerAvailabilityMatch,
  AppointmentPlanningPreviewAppointment,
  AppointmentPlanningResolvedAppointmentContact,
} from "../../../../../../api/kehrblatt/planning"
import { buildAppointmentTimeTagPresentation } from "../../../../../../_shared/_logic/appointment-time-tags"
import { buildScheduleEventCommunicationTagPresentation } from "../../../../../../_shared/_logic/communication-presentation"
import {
  buildRichTooltip,
  tooltipField,
  tooltipText,
} from "../../../../../../_shared/_logic/rich-tooltips"
import type { BatchTagPresentation } from "../../../_logic/appointments-contract"

const dateTimeFormatter = new Intl.DateTimeFormat("de-DE", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Berlin",
})

const timeFormatter = new Intl.DateTimeFormat("de-DE", {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Berlin",
})

const shortDateFormatter = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  timeZone: "Europe/Berlin",
})

const dateFormatter = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "Europe/Berlin",
})

const parseAppointmentDate = (value?: string | null): Date | null => {
  if (!value) return null

  const parsed = new Date(value)

  return Number.isFinite(parsed.getTime()) ? parsed : null
}

const sameAppointmentDay = (left: Date, right: Date): boolean =>
  dateFormatter.format(left) === dateFormatter.format(right)

const compactAppointmentWindow = (
  startAt?: string | null,
  endAt?: string | null,
): string | null => {
  const start = parseAppointmentDate(startAt)

  if (!start) return null

  const end = parseAppointmentDate(endAt)

  if (!end) return `${shortDateFormatter.format(start)} ${timeFormatter.format(start)}`

  if (sameAppointmentDay(start, end)) {
    return `${shortDateFormatter.format(start)} ${timeFormatter.format(start)}-${timeFormatter.format(end)}`
  }

  return `${shortDateFormatter.format(start)} ${timeFormatter.format(start)}-${shortDateFormatter.format(end)} ${timeFormatter.format(end)}`
}

const compactAppointmentTimeRange = (
  startAt?: string | null,
  endAt?: string | null,
): string | null => {
  const start = parseAppointmentDate(startAt)
  const end = parseAppointmentDate(endAt)

  if (!start) return null

  if (!end) return timeFormatter.format(start)

  if (sameAppointmentDay(start, end))
    return `${timeFormatter.format(start)}-${timeFormatter.format(end)}`

  return compactAppointmentWindow(startAt, endAt)
}

export const appointmentContactLabel = (
  contact?: AppointmentPlanningResolvedAppointmentContact | null,
): string | null => {
  const label = String(contact?.display_name ?? "").trim()

  return label || null
}

const customerAvailabilityWindowLabel = (
  match?: AppointmentPlanningCustomerAvailabilityMatch | null,
): string | null => {
  if (!match?.matched_window_start_at || !match.matched_window_end_at) return null

  const start = new Date(match.matched_window_start_at)
  const end = new Date(match.matched_window_end_at)

  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return null

  return `${dateTimeFormatter.format(start)} bis ${timeFormatter.format(end)} Uhr`
}

const customerAvailabilityMatchLabel = (
  match?: AppointmentPlanningCustomerAvailabilityMatch | null,
): string | null => {
  switch (match?.tier) {
    case "INSIDE_WINDOW":
      return "Kontaktzeit passt"
    case "PARTIAL_OVERLAP":
      return "Kontaktzeit teilweise"
    case "NEAR_WINDOW":
      return "Nahe Kontaktzeit"
    case "OUTSIDE_WINDOW":
      return "Außerhalb Kontaktzeit"
    default:
      return null
  }
}

const appointmentSlotBounds = (
  appointment: AppointmentPlanningPreviewAppointment,
  isFlex: boolean,
) => {
  const hasCompleteFlexTimeframe = Boolean(
    appointment.timeframe_start_at && appointment.timeframe_end_at,
  )

  return isFlex && hasCompleteFlexTimeframe
    ? { startAt: appointment.timeframe_start_at, endAt: appointment.timeframe_end_at }
    : { startAt: appointment.start_at, endAt: appointment.end_at }
}

export const buildAppointmentTag = (
  appointment: AppointmentPlanningPreviewAppointment | null | undefined,
  employeeLabelById: Record<string, string>,
  resolvedContact?: AppointmentPlanningResolvedAppointmentContact | null,
  customerAvailabilityMatch?: AppointmentPlanningCustomerAvailabilityMatch | null,
  existing?: { keep: boolean; event?: ScheduleEvent | null },
): BatchTagPresentation => {
  if (!appointment) {
    return {
      label: "Kein Termin",
      subLabel: "Nicht planbar",
      color: "rose",
      icon: mdiMinusCircleOutline,
      tooltip: "Für diese Zeile wurde kein freier Termin gefunden.",
    }
  }

  const timeTag = buildAppointmentTimeTagPresentation(appointment)
  const slot = appointmentSlotBounds(appointment, timeTag.isFlex)
  const contactLabel = appointmentContactLabel(resolvedContact)
  const contactTimeLabel = customerAvailabilityWindowLabel(customerAvailabilityMatch)
  const contactMatchLabel = customerAvailabilityMatchLabel(customerAvailabilityMatch)
  const customerWindowLabel = compactAppointmentWindow(
    appointment.customer_window_start_at,
    appointment.customer_window_end_at,
  )
  const communicatedLabel =
    customerWindowLabel ?? compactAppointmentWindow(slot.startAt, slot.endAt) ?? timeTag.baseLabel
  const slotLabel = compactAppointmentTimeRange(slot.startAt, slot.endAt)
  const existingEvent = existing?.event
  const communication =
    existing?.keep &&
    existingEvent &&
    Date.parse(existingEvent.start_at) === Date.parse(appointment.start_at) &&
    Date.parse(existingEvent.end_at) === Date.parse(appointment.end_at)
      ? buildScheduleEventCommunicationTagPresentation({
          status: existingEvent.status,
          event: existingEvent,
        })
      : null

  return {
    label: communicatedLabel,
    subLabel:
      communication?.label ??
      (existing?.keep ? "Bestehender Termin · Status unbekannt" : "Terminvorschlag"),
    color: communication?.definition.color ?? "neutral",
    icon: communication?.definition.icon ?? mdiCalendarClockOutline,
    tooltip:
      (communication?.tooltip ?? "") +
      buildRichTooltip([
        customerWindowLabel && slotLabel ? tooltipField("Kalenderzeit", slotLabel) : null,
        tooltipField("Dauer", timeTag.durationLabel),
        contactLabel ? tooltipField("Ansprechpartner", contactLabel) : null,
        contactTimeLabel ? tooltipField("Verfügbar", contactTimeLabel) : null,
        customerAvailabilityMatch?.tier === "OUTSIDE_WINDOW"
          ? tooltipText("Der vorgeschlagene Termin liegt außerhalb der angegebenen Verfügbarkeit.")
          : customerAvailabilityMatch?.tier === "PARTIAL_OVERLAP"
            ? tooltipText("Der Termin passt nur teilweise zur angegebenen Verfügbarkeit.")
            : customerAvailabilityMatch?.tier === "NEAR_WINDOW"
              ? tooltipText("Der Termin liegt kurz vor oder nach der angegebenen Verfügbarkeit.")
              : null,
        timeTag.tooltipDescription ? tooltipText(timeTag.tooltipDescription) : null,
      ]),
    tooltipHtml: true,
  }
}
